import { Button, Card, Flex, Form, type FormProps, Input, Select, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { type ReactNode, useEffect, useMemo } from 'react';

import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import { MealCategory } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import { useMutation, useQuery } from '../../../jsonapi-provider';
import type ProductionLine from '../../../entities/production-line';

const SUCCESS_UPDATE_MESSAGE = 'Production line updated successfully!';
const SUCCESS_ADD_MESSAGE = 'Production line added successfully!';

interface DataType {
  productionLine: ProductionLine;
  onSuccess: (productionLine: ProductionLine) => void;
  onCancel: () => void;
}

const ProductionLineForm = (props: DataType): ReactNode => {
  const notification = useNotification();
  const { productionLine, onSuccess, onCancel } = props;
  const [formInstance] = Form.useForm();
  const isEditMode = useMemo(() => !!productionLine?.id, [productionLine?.id]);
  const [mutateProductionLine, productionLines] = useMutation<ProductionLine>(
    isEditMode ? ['production-line', productionLine.id] : ['production-line']
  );
  const initialValues = useMemo(
    () => ({
      ...productionLine,
    }),
    [productionLine]
  );
  const mealCategories = useQuery<MealCategory[]>([
    'meal-category',
    {
      fields: {
        'meal-category': ['name'],
      },
      sort: 'sortOrder',
      page: {
        size: 10000,
      },
    },
  ]);
  const routes = Array(99)
    .fill(0)
    .map((_, i) => ({ id: i + 1 }));

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const onSubmit: FormProps<ProductionLine>['onFinish'] = (submitted) => {
    from(mutateProductionLine(submitted))
      .pipe(
        switchMap((res) =>
          res.error ? throwError(() => new Error(res.error?.title)) : of(res.data)
        ),
        tap((ct) => {
          notification.success({
            description: isEditMode ? SUCCESS_UPDATE_MESSAGE : SUCCESS_ADD_MESSAGE,
            message: '',
          });
          onSuccess(ct!);
        }),
        catchError((error: Error) => {
          const [description, title, ..._err] = error?.message.split('\n');
          notification.error({
            description: description ?? 'No production lines are available',
            message: title ?? '',
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={formInstance}
          id="cycle-menu-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item>
            <Typography.Title level={4}>
              {isEditMode ? productionLine.name : 'New Production Line'}
            </Typography.Title>
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item
            name="mealCategories"
            label="Meal Categories"
            rules={[{ required: true }]}
            getValueProps={(mealCategories: MealCategory[]) => ({
              value: mealCategories?.map((mealCategory) => mealCategory.id) ?? [],
            })}
            normalize={(value: string[]) => value?.map((id) => new MealCategory(id))}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="name"
              mode="multiple"
              options={mealCategories.data ?? []}
              placeholder="Select Meal Categories"
              fieldNames={{ label: 'name', value: 'id' }}
            />
          </Form.Item>

          <Form.Item
            name="hhcRoutesNumbers"
            label="HHC Routes Numbers"
            rules={[{ required: true }]}
          >
            <Select
              allowClear
              showSearch
              mode="multiple"
              options={routes}
              placeholder="Select HHC routes numbers"
              fieldNames={{ label: 'id', value: 'id' }}
            />
          </Form.Item>

          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse' }}>
              <Button
                block
                type="primary"
                disabled={productionLines.isLoading}
                loading={productionLines.isLoading}
                htmlType="submit"
              >
                Save
              </Button>
              <Button block onClick={onCancel} disabled={productionLines.isLoading}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

ProductionLineForm.propTypes = {
  defaultPageSize: PropTypes.number,
};

ProductionLineForm.defaultProps = {
  defaultPageSize: 10,
  onVisibleColumnsChange: () => {},
  onSelectRowsChange: () => {},
};

ProductionLineForm.displayName = 'EditProductionLineSection';
export default ProductionLineForm;
